import { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Field,
  Input,
  Option,
  Spinner,
  Switch,
} from '@fluentui/react-components';
import {
  SearchRegular,
  PersonRegular,
  BuildingRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createDropoffTaxReturn, searchDataverse } from '../services/api';
import type { ContactInfo, AccountInfo, SearchResults } from '../types';
import { amendmentToggleLabel } from '../types';

const currentYear = new Date().getFullYear();
const defaultTaxYear = currentYear - 1;
const yearOptions = [defaultTaxYear, defaultTaxYear - 1, defaultTaxYear - 2];

export interface CreateDropoffReturnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preparerId?: string;
  preparerName?: string;
}

export default function CreateDropoffReturnDialog({
  open,
  onOpenChange,
  preparerId,
  preparerName,
}: CreateDropoffReturnDialogProps) {
  const queryClient = useQueryClient();
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [taxYear, setTaxYear] = useState(defaultTaxYear);
  const [returnType, setReturnType] = useState<'individual' | 'business'>('individual');
  const [isAmendment, setIsAmendment] = useState(false);

  // Dataverse search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | undefined>();
  const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>();
  const [linkedRecordName, setLinkedRecordName] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: createDropoffTaxReturn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturns'] });
      resetForm();
      onOpenChange(false);
    },
  });

  const resetForm = () => {
    setClientName('');
    setClientEmail('');
    setClientPhone('');
    setTaxYear(defaultTaxYear);
    setReturnType('individual');
    setIsAmendment(false);
    setSearchQuery('');
    setSearchResults(null);
    setSelectedContactId(undefined);
    setSelectedAccountId(undefined);
    setLinkedRecordName(null);
  };

  // Debounced Dataverse search
  const performSearch = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setSearchResults(null);
      return;
    }
    setIsSearching(true);
    try {
      const results = await searchDataverse(query.trim());
      setSearchResults(results);
    } catch {
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        performSearch(searchQuery);
      } else {
        setSearchResults(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, performSearch]);

  const handleSelectContact = (contact: ContactInfo) => {
    setClientName(contact.fullName);
    setClientEmail(contact.email || '');
    setClientPhone(contact.phone || contact.mobilePhone || '');
    setSelectedContactId(contact.contactId);
    setSelectedAccountId(undefined);
    setLinkedRecordName(contact.fullName);
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleSelectAccount = (account: AccountInfo) => {
    setClientName(account.name);
    setClientEmail(account.email || '');
    setClientPhone(account.phone || '');
    setSelectedAccountId(account.accountId);
    setSelectedContactId(undefined);
    setReturnType('business');
    setIsAmendment(false);
    setLinkedRecordName(account.name);
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleClearLinked = () => {
    setSelectedContactId(undefined);
    setSelectedAccountId(undefined);
    setLinkedRecordName(null);
  };

  const handleSubmit = () => {
    createMutation.mutate({
      clientName: clientName.trim(),
      clientEmail: clientEmail.trim() || undefined,
      clientPhone: clientPhone.trim() || undefined,
      taxYear,
      returnType,
      preparerId,
      preparerName,
      contactId: selectedContactId,
      accountId: selectedAccountId,
      isAmendment,
    });
  };

  const handleOpenChange = (_: unknown, data: { open: boolean }) => {
    onOpenChange(data.open);
    if (!data.open) {
      resetForm();
      createMutation.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Create Dropoff / Walk-in Return</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Dataverse Search */}
              <Field label="Search Dataverse" hint="Search for an existing client to auto-fill details">
                <Input
                  value={searchQuery}
                  onChange={(_, data) => setSearchQuery(data.value)}
                  placeholder="Search by name, email, or phone..."
                  contentBefore={<SearchRegular />}
                  contentAfter={isSearching ? <Spinner size="tiny" /> : undefined}
                />
              </Field>

              {/* Linked record indicator */}
              {linkedRecordName && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#dff6dd',
                  borderRadius: '6px',
                  border: '1px solid #107c10',
                }}>
                  {selectedAccountId ? <BuildingRegular /> : <PersonRegular />}
                  <span style={{ flex: 1, fontSize: '13px', fontWeight: 500 }}>
                    Linked to: {linkedRecordName}
                  </span>
                  <Button
                    appearance="subtle"
                    icon={<DismissRegular />}
                    size="small"
                    onClick={handleClearLinked}
                  />
                </div>
              )}

              {/* Search results dropdown */}
              {searchResults && (searchResults.contacts.length > 0 || searchResults.accounts.length > 0) && (
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid var(--tl-border)',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                }}>
                  {searchResults.contacts.length > 0 && (
                    <>
                      <div style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 600, color: 'var(--tl-text-secondary)', backgroundColor: 'var(--tl-bg-page)' }}>
                        Contacts
                      </div>
                      {searchResults.contacts.map((contact) => (
                        <div
                          key={contact.contactId}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--tl-bg-subtle)',
                          }}
                          onClick={() => handleSelectContact(contact)}
                          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--tl-bg-subtle)')}
                          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <PersonRegular style={{ color: '#0078d4' }} />
                            <div>
                              <div style={{ fontWeight: 500, fontSize: '13px' }}>{contact.fullName}</div>
                              <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)' }}>
                                {[contact.email, contact.phone].filter(Boolean).join(' · ')}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {searchResults.accounts.length > 0 && (
                    <>
                      <div style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 600, color: 'var(--tl-text-secondary)', backgroundColor: 'var(--tl-bg-page)' }}>
                        Accounts
                      </div>
                      {searchResults.accounts.map((account) => (
                        <div
                          key={account.accountId}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--tl-bg-subtle)',
                          }}
                          onClick={() => handleSelectAccount(account)}
                          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--tl-bg-subtle)')}
                          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <BuildingRegular style={{ color: '#107c10' }} />
                            <div>
                              <div style={{ fontWeight: 500, fontSize: '13px' }}>{account.name}</div>
                              <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)' }}>
                                {[account.email, account.phone].filter(Boolean).join(' · ')}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* Search no results */}
              {searchResults && searchResults.contacts.length === 0 && searchResults.accounts.length === 0 && searchQuery.trim().length >= 2 && (
                <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', fontStyle: 'italic' }}>
                  No results found. You can still enter details manually.
                </div>
              )}

              <Field label="Client Name" required>
                <Input
                  value={clientName}
                  onChange={(_, data) => setClientName(data.value)}
                  placeholder="First and Last Name"
                />
              </Field>
              <Field label="Email">
                <Input
                  type="email"
                  value={clientEmail}
                  onChange={(_, data) => setClientEmail(data.value)}
                  placeholder="client@example.com"
                />
              </Field>
              <Field label="Phone">
                <Input
                  type="tel"
                  value={clientPhone}
                  onChange={(_, data) => setClientPhone(data.value)}
                  placeholder="(239) 555-0123"
                />
              </Field>
              <Field label="Tax Year">
                <Dropdown
                  value={taxYear.toString()}
                  selectedOptions={[taxYear.toString()]}
                  onOptionSelect={(_, data) => setTaxYear(Number(data.optionValue))}
                >
                  {yearOptions.map((year) => (
                    <Option key={year} value={year.toString()}>
                      {year.toString()}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="Return Type">
                <Dropdown
                  value={returnType === 'business' ? 'Business' : 'Individual (1040)'}
                  selectedOptions={[returnType]}
                  onOptionSelect={(_, data) => {
                    const next = data.optionValue as 'individual' | 'business';
                    setReturnType(next);
                  }}
                >
                  <Option value="individual">Individual (1040)</Option>
                  <Option value="business">Business</Option>
                </Dropdown>
              </Field>
              <Switch
                checked={isAmendment}
                onChange={(_, data) => setIsAmendment(data.checked)}
                label={amendmentToggleLabel(returnType)}
              />
              {createMutation.isError && (
                <div style={{ color: '#a4262c', fontSize: '13px' }}>
                  {createMutation.error?.message || 'Failed to create return'}
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!clientName.trim() || createMutation.isPending}
              onClick={handleSubmit}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Return'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
